import { atom } from 'jotai';
import { availableRoles } from '@studio/constants/roles';
import { sortByAlphaIgnoreCase } from '@studio/helpers/sortByAlphaIgnoreCase';
import type { CachedFetchLoadInFlight, StoreData } from '@studio/helpers/tsUtils';
import type { Roles, Users } from '@studio/types';

export const usersAtom = atom<StoreData<Readonly<Users.Get.User[]>>>({
  data: [],
  error: undefined,
  hasFetched: false,
  timestamp: 0,
});

export const userInvitesAtom = atom<StoreData<Readonly<Users.Get.UserInvite[]>>>({
  data: [],
  error: undefined,
  hasFetched: false,
  timestamp: 0,
});

export const userRolesAtom = atom<StoreData<Readonly<Roles.Get.Role[]>>>({
  data: [],
  error: undefined,
  hasFetched: false,
  timestamp: 0,
});

export const usersLoadInFlightAtom = atom<CachedFetchLoadInFlight | null>(null);

export const userInvitesLoadInFlightAtom = atom<CachedFetchLoadInFlight | null>(null);

export const userRolesLoadInFlightAtom = atom<CachedFetchLoadInFlight | null>(null);

export const usersFilterAtom = atom('');

export const filteredUserRolesAtom = atom<Roles.Get.Role[]>((get) => {
  const { data: roles } = get(userRolesAtom);
  return roles.filter((role) => availableRoles.some((includedRole) => role.id === includedRole));
});

export const filteredUsersAtom = atom<Users.Get.UserOrInvite[]>((get) => {
  const { data: users } = get(usersAtom);
  const { data: invites } = get(userInvitesAtom);
  const filter = get(usersFilterAtom);
  const sortedInvites = invites
    .filter((item) => item.email.toLowerCase().includes(filter.toLowerCase()))
    .sort(sortByAlphaIgnoreCase((invite: Users.Get.UserInvite) => invite.email));
  const sortedUsers = users
    .filter((item) => item.userName.toLowerCase().includes(filter.toLowerCase()))
    .sort(sortByAlphaIgnoreCase((user: Users.Get.User) => user.userName));
  return [...sortedInvites, ...sortedUsers];
});

export const invalidateUsersCacheAtom = atom(null, (_get, set) => {
  set(usersAtom, (prev) => ({ ...prev, hasFetched: false, timestamp: 0 }));
});

export const invalidateUserInvitesCacheAtom = atom(null, (_get, set) => {
  set(userInvitesAtom, (prev) => ({ ...prev, hasFetched: false, timestamp: 0 }));
});

export const invalidateUserRolesCacheAtom = atom(null, (_get, set) => {
  set(userRolesAtom, (prev) => ({ ...prev, hasFetched: false, timestamp: 0 }));
});
